import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

import api from "../../services/api";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { Confirm, FormError, ModalFoot, PanelBody } from "../../components/ui/panelKit";

const LABELS = { name: "Nomi", parent_id: "Ustki guruh" };

/**
 * Flatten the client's groups into the order they are drawn in, depth first.
 *
 * The endpoint returns a flat list sorted by name, which tells a reader
 * nothing about what sits under what. Ordering is done here rather than asked
 * of the server because the panel needs the parent/child relation anyway, for
 * the delete confirmation.
 */
export const toTree = (list) => {
  const children = new Map();
  list.forEach((g) => {
    const key = g.parent_id || "";
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(g);
  });

  const out = [];
  const walk = (parentKey, depth) => {
    (children.get(parentKey) || []).forEach((g) => {
      out.push({ ...g, depth });
      walk(g.id, depth + 1);
    });
  };
  walk("", 0);

  // A group whose parent is missing from the payload would otherwise vanish
  // from the screen entirely. Nothing should produce that, but a silently
  // shorter list is the worst way to find out it did.
  if (out.length !== list.length) {
    const seen = new Set(out.map((g) => g.id));
    list.filter((g) => !seen.has(g.id)).forEach((g) => out.push({ ...g, depth: 0 }));
  }
  return out;
};

/** Every group at or below `id`, which is exactly what a delete takes with it. */
const subtreeOf = (rows, id) => {
  const found = [];
  const collect = (parentId) => {
    rows
      .filter((g) => g.parent_id === parentId)
      .forEach((child) => {
        found.push(child);
        collect(child.id);
      });
  };
  const self = rows.find((g) => g.id === id);
  if (self) found.push(self);
  collect(id);
  return found;
};

/**
 * The folder tree this client's threads live in.
 *
 * Subgroups organise threads; they do not authorise. Deleting one is the
 * heaviest action on this page: it hides the whole subtree AND every thread and
 * message inside it, which is the only way a thread ever becomes hidden. The
 * confirmation therefore counts what would go before offering the button --
 * nobody should be able to empty a populated group without being told what is
 * in it.
 */
const GroupsPanel = ({ clientId, onGroupsChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/groups/`, { params: { client: clientId } });
      const tree = toTree(res.data.results || []);
      setRows(tree);
      // The members panel's group select is this same tree; it must not be
      // able to offer a group that was just deleted.
      onGroupsChange?.(tree);
    } catch (err) {
      setError(readApiError(err, LABELS, "Guruhlar roʻyxatini yuklab boʻlmadi."));
    } finally {
      setLoading(false);
    }
  }, [clientId, onGroupsChange]);

  useEffect(() => {
    load();
  }, [load]);

  const root = rows.find((g) => g.is_root);

  const openAdd = (parent) => {
    setName("");
    setParentId(parent?.id || root?.id || "");
    setFormError("");
    setOpen(true);
  };

  const add = async () => {
    const value = name.trim();
    if (!value) {
      setFormError("Guruh nomini kiriting.");
      return;
    }
    if (!parentId) {
      setFormError("Ustki guruhni tanlang. Har bir guruh boshqa guruh ostida boʻladi.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.post(`/groups/`, { name: value, parent_id: parentId });
      notify.success("Guruh qoʻshildi");
      setOpen(false);
      setName("");
      await load();
    } catch (err) {
      setFormError(readApiError(err, LABELS, "Guruhni qoʻshib boʻlmadi."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.delete(`/groups/${confirm.group.id}/`);
      notify.success("Guruh va uning ostidagi hamma narsa yashirildi");
      setConfirm(null);
      await load();
    } catch (err) {
      notify.error(readApiError(err, LABELS, "Guruhni oʻchirib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  /** What this delete would take with it, counted before it is offered. */
  const askRemove = (group) => {
    const subtree = subtreeOf(rows, group.id);
    setConfirm({
      group,
      groups: subtree.length - 1,
      threads: subtree.reduce((n, g) => n + (g.thread_count || 0), 0),
      messages: subtree.reduce((n, g) => n + (g.message_count || 0), 0),
    });
  };

  const confirmMessage = () => {
    if (!confirm) return "";
    const parts = [];
    if (confirm.groups) parts.push(`${confirm.groups} ta ichki guruh`);
    if (confirm.threads) parts.push(`${confirm.threads} ta muhokama`);
    if (confirm.messages) parts.push(`${confirm.messages} ta xabar`);
    if (!parts.length)
      return "Guruh boʻsh: ichida muhokama ham, xabar ham yoʻq. Shunga qaramay, guruhni qaytarish uchun panelda tugma yoʻq.";
    return `Bu guruh bilan birga ${parts.join(", ")} ham yashiriladi. Ular hech kimga koʻrinmaydi va panelda qaytarish tugmasi yoʻq.`;
  };

  return (
    <div className="ms-panel">
      <div className="ms-panel-head">
        <h2>Guruhlar</h2>
        <div className="ms-toolbar-spacer" />
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--sm"
          onClick={() => openAdd(root)}
          disabled={!root}
          title={root ? "" : "Ildiz guruh yuklanmagan"}
        >
          <Icon icon="tabler:folder-plus" />
          Guruh
        </button>
      </div>

      <PanelBody
        loading={loading}
        error={error}
        empty={!rows.length}
        emptyText="Guruhlar yoʻq"
      >
        <div className="ms-table-wrap">
          <table className="ms-table ms-table--compact">
            <thead>
              <tr>
                <th>Nomi</th>
                <th style={{ width: 120 }}>Muhokamalar</th>
                <th style={{ width: 110 }}>Xabarlar</th>
                <th className="ms-td-actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id}>
                  {/* Depth is data, so it is drawn inline rather than by
                      nesting lists -- nesting would indent the numbers too and
                      they would stop lining up. */}
                  <td className="ms-td-strong" style={{ paddingInlineStart: 12 + g.depth * 18 }}>
                    {g.depth > 0 && <span style={{ opacity: 0.4 }}>— </span>}
                    {g.name}
                    {g.is_root && (
                      <span className="ms-badge ms-badge--neutral" style={{ marginInlineStart: 8 }}>
                        ildiz
                      </span>
                    )}
                  </td>
                  <td className="ms-td-num">{g.thread_count ?? 0}</td>
                  <td className="ms-td-num">{g.message_count ?? 0}</td>
                  <td className="ms-td-actions">
                    <div className="ms-rowacts">
                      <button
                        type="button"
                        className="ms-rowact is-edit"
                        title="Ichki guruh qoʻshish"
                        aria-label={`${g.name} ostiga ichki guruh qoʻshish`}
                        onClick={() => openAdd(g)}
                      >
                        <Icon icon="tabler:plus" />
                      </button>
                      {/* The root group is what membership is granted on, so
                          the schema forbids removing it. No button rather than
                          a button that always fails. */}
                      {!g.is_root && (
                        <button
                          type="button"
                          className="ms-rowact is-danger"
                          title="Oʻchirish"
                          aria-label={`${g.name} guruhini oʻchirish`}
                          onClick={() => askRemove(g)}
                        >
                          <Icon icon="tabler:trash" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelBody>

      <Modal
        open={open}
        title="Guruh qoʻshish"
        size="sm"
        onClose={() => setOpen(false)}
        footer={
          <ModalFoot
            onCancel={() => setOpen(false)}
            onSave={add}
            saving={saving}
            saveLabel="Qoʻshish"
            savingLabel="Qoʻshilmoqda..."
          />
        }
      >
        <div className="ms-form-grid ms-form-grid--stack">
          <div className="ms-field ms-field--full">
            <label htmlFor="ms-group-parent">
              Ustki guruh <span>*</span>
            </label>
            <select
              id="ms-group-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              {rows.map((g) => (
                <option key={g.id} value={g.id}>
                  {"— ".repeat(g.depth)}
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ms-field ms-field--full">
            <label htmlFor="ms-group-name">
              Nomi <span>*</span>
            </label>
            <input
              id="ms-group-name"
              type="text"
              placeholder="Masalan: Buxgalteriya"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
          </div>

          <FormError>{formError}</FormError>
        </div>
      </Modal>

      <Confirm
        open={!!confirm}
        busy={busy}
        name={confirm?.group.name}
        title="Guruhni oʻchirish"
        message={confirmMessage()}
        onConfirm={remove}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default GroupsPanel;
